import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarOff, CalendarRange, Clock, Fingerprint, Plane, ScanFace, Sunrise } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { FichaProveedor, display } from './ui';
import HoyVista from './vistas/HoyVista';
import PeriodoVista from './vistas/PeriodoVista';
import MarcajesVista from './vistas/MarcajesVista';
import AusenciasVista from './vistas/AusenciasVista';
import AparatosVista from './vistas/AparatosVista';
import HorariosVista from './vistas/HorariosVista';
import FeriadosVista from './vistas/FeriadosVista';
import FichaPersona from './FichaPersona';
import './asistencia.css';

const SECCIONES = [
    {
        titulo: null,
        vistas: [
            { id: 'hoy', label: 'Hoy', icono: Sunrise, componente: HoyVista },
            { id: 'periodo', label: 'Período', icono: CalendarRange, componente: PeriodoVista },
            { id: 'ausencias', label: 'Vacaciones y permisos', icono: Plane, componente: AusenciasVista },
            { id: 'marcajes', label: 'Marcajes', icono: Fingerprint, componente: MarcajesVista },
        ],
    },
    {
        titulo: 'Configuración',
        vistas: [
            { id: 'aparatos', label: 'Aparatos', icono: ScanFace, componente: AparatosVista },
            { id: 'horarios', label: 'Horarios', icono: Clock, componente: HorariosVista },
            { id: 'feriados', label: 'Días no laborables', icono: CalendarOff, componente: FeriadosVista },
        ],
    },
];
const VISTAS = SECCIONES.flatMap((s) => s.vistas);

// Cuántos aparatos están en línea: se ve siempre, arriba de la navegación
const EstadoAparatos = ({ onIr }) => {
    const [aparatos, setAparatos] = useState(null);
    useEffect(() => {
        let vivo = true;
        const cargar = () => axiosInstance.get('/api/asistencia/estado')
            .then(({ data }) => vivo && setAparatos(data.aparatos)).catch(() => {});
        cargar();
        const t = setInterval(cargar, 20000);
        return () => { vivo = false; clearInterval(t); };
    }, []);
    if (!aparatos) return <div className="h-14" />;
    const enLinea = aparatos.filter((a) => a.conectado).length;
    return (
        <button
            type="button" onClick={onIr}
            className="w-full text-left rounded-xl px-3 py-2.5 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
            title={aparatos.map((a) => `${a.nombre}: ${a.conectado ? 'en línea' : 'sin conexión'}`).join('\n')}
        >
            <div className="flex items-center gap-1.5" aria-hidden>
                {aparatos.map((a) => (
                    <span key={a.nombre} className={`h-1.5 flex-1 rounded-full ${a.conectado ? 'bg-cyan-400' : 'bg-slate-200'}`} />
                ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
                {enLinea === aparatos.length ? 'Todos los aparatos en línea' : `${enLinea} de ${aparatos.length} aparatos en línea`}
            </p>
        </button>
    );
};

const AsistenciaView = () => {
    const [params, setParams] = useSearchParams();
    const actual = VISTAS.find((v) => v.id === params.get('vista')) || VISTAS[0];
    const [ficha, setFicha] = useState(null);   // { id, mes }
    const ir = useCallback((id) => setParams(id === 'hoy' ? {} : { vista: id }), [setParams]);
    const abrirFicha = useCallback((id, mes) => setFicha({ id, mes }), []);

    const Vista = actual.componente;
    return (
        <FichaProveedor value={abrirFicha}>
            <div className="bg-[#F6F7F9] flex -m-4 md:-m-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[calc(100%+2rem)] md:h-[calc(100%+4rem)] overflow-hidden">
                {/* Navegación del módulo */}
                <aside className="hidden lg:flex w-60 shrink-0 flex-col bg-white border-r border-slate-200/80">
                    <div className="px-5 pt-6 pb-3">
                        <h1 className={`${display} text-xl font-bold text-sura-blue`}>Asistencia</h1>
                    </div>
                    <div className="px-2">
                        <EstadoAparatos onIr={() => ir('aparatos')} />
                    </div>
                    <nav className="flex-1 overflow-y-auto px-3 pt-4 pb-6 space-y-6" aria-label="Asistencia">
                        {SECCIONES.map((s, i) => (
                            <div key={i}>
                                {s.titulo && <p className="px-3 mb-1.5 text-xs font-medium text-slate-400">{s.titulo}</p>}
                                <ul className="space-y-0.5">
                                    {s.vistas.map((v) => {
                                        const activa = v.id === actual.id;
                                        const Icono = v.icono;
                                        return (
                                            <li key={v.id}>
                                                <button
                                                    type="button" onClick={() => ir(v.id)} aria-current={activa ? 'page' : undefined}
                                                    className={`relative w-full flex items-center gap-2.5 h-9 px-3 rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${
                                                        activa ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                                    }`}
                                                >
                                                    {activa && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-sura-blue" />}
                                                    <Icono size={16} className={activa ? 'text-sura-blue' : 'text-slate-400'} />
                                                    {v.label}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </nav>
                </aside>

                {/* Contenido */}
                <main className="flex-1 min-w-0 overflow-y-auto scroll-fino">
                    {/* En pantallas chicas la navegación va arriba */}
                    <div className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200/80 px-4 py-3">
                        <label className="sr-only" htmlFor="asistencia-vista">Sección</label>
                        <select
                            id="asistencia-vista" value={actual.id} onChange={(e) => ir(e.target.value)}
                            className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-800"
                        >
                            {SECCIONES.map((s, i) => (
                                <optgroup key={i} label={s.titulo || 'Asistencia'}>
                                    {s.vistas.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                                </optgroup>
                            ))}
                        </select>
                    </div>
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
                        <Vista ir={ir} />
                    </div>
                </main>
            </div>
            {ficha && <FichaPersona id={ficha.id} mesInicial={ficha.mes} onCerrar={() => setFicha(null)} />}
        </FichaProveedor>
    );
};

export default AsistenciaView;
